import { MutableRefObject, useEffect, useRef, useState } from 'react'
import {
	createStreamSupervisor,
	StreamSupervisorOptions,
} from './stream-supervisor'
import { usePageReturn } from './use-page-return'

/** How often a stream is checked for silence. */
export const WATCH_EVERY_MS = 5_000

export type SupervisedStream = {
	/** The stream open now: a ref, so a reopen is seen without a render. */
	streamRef: MutableRefObject<EventSource | undefined>
	/** Replaces the stream now. */
	reopen: () => void
}

/**
 * `createStreamSupervisor` for a component: a new stream whenever `open`
 * changes - the room has moved somewhere else - a check for silence every
 * few seconds, and a check whenever the participant comes back to the page.
 */
export const useStreamSupervisor = (
	options: StreamSupervisorOptions
): SupervisedStream => {
	const latest = useRef(options)
	latest.current = options
	const streamRef = useRef<EventSource>()
	const [supervisor] = useState(() =>
		createStreamSupervisor(() => latest.current, streamRef)
	)
	const { open } = options

	useEffect(() => {
		supervisor.connect()
		return supervisor.stop
	}, [open, supervisor])

	useEffect(() => {
		const timer = setInterval(supervisor.watch, WATCH_EVERY_MS)
		return () => clearInterval(timer)
	}, [supervisor])

	usePageReturn(supervisor.onReturn)

	return { streamRef, reopen: supervisor.reopen }
}
